/**
 * 공간별 소계 — 「욕실만 얼마예요?」 의 답.
 *
 * 상담에서 고객이 제일 많이 하는 질문인데, 지금까지는 뽑을 수가 없었다. 견적은
 * **공종(누가 하나)** 으로만 묶여 있어서 「욕실」 은 도배·타일·설비·바스에 흩어져
 * 있었다. 공간은 그 축을 가로지른다.
 *
 * 저장소도 화면도 모르는 순수 함수로 둔다 — 편집기(저장 전)와 상세·인쇄(저장 후)가
 * 같은 값을 보여야 하므로 한 자리에서만 센다.
 *
 * **SET 부모는 세지 않는다.** 부모 금액은 자식 합이라 같이 더하면 두 번 센다.
 */

/** 셀 수 있는 최소한만 받는다 — QuoteItem 이든 스냅샷 항목이든 이 모양이면 된다. */
export interface SpaceCountable {
  space_code: string | null;
  amount: number;
  material_amount: number;
  labor_amount: number;
  /** 있으면 SET 부모다. 부모는 자기 금액을 세지 않고 자식이 센다. */
  children?: SpaceCountable[] | null;
}

export interface SpaceTotal {
  /** null 이면 「공간을 안 정한 항목」 묶음이다. */
  code: string | null;
  label: string;
  amount: number;
  material_amount: number;
  labor_amount: number;
  /** 이 공간에 속한 항목 수(SET 부모 제외). */
  count: number;
}

/** 공간을 안 정한 묶음의 표기. 빈칸으로 두면 표에서 줄이 사라진 것처럼 읽힌다. */
export const UNASSIGNED_SPACE_LABEL = "공간 미지정";

/**
 * 셀 수 있는 줄만 남긴다.
 *
 * SET 부모는 건너뛰고 자식만 센다 — 부모 금액은 자식 합이라 같이 더하면 두 번
 * 센다. 다만 **부모에 적어 둔 공간은 자식이 물려받는다**: 사람은 「주방 가구
 * SET」 에 한 번만 적지, 자식 줄마다 다시 고르지 않는다. 물려받지 않으면 그
 * 견적은 통째로 「미지정」 으로 떨어진다.
 */
const flatten = (
  items: readonly SpaceCountable[],
  inherited: string | null = null
): SpaceCountable[] =>
  items.flatMap((item) => {
    const own = item.space_code || inherited;
    const children = item.children ?? [];
    return children.length > 0
      ? flatten(children, own)
      : [{ ...item, space_code: own }];
  });

/**
 * 공간별로 묶어 금액을 센다.
 *
 * 순서는 **회사가 정한 공간 목록 순서**를 따른다(`spaces`). 금액 큰 순으로
 * 정렬하면 견적을 고칠 때마다 줄이 튀어 읽기 어렵다. 목록에 없는 코드는 뒤에
 * 붙이고, 공간을 안 정한 항목은 맨 끝에 모은다.
 */
export const spaceTotals = (
  items: readonly SpaceCountable[],
  /** 회사의 공간 코드값. `{ code, label }` 순서가 곧 표의 순서다. */
  spaces: readonly { code: string; label: string }[]
): SpaceTotal[] => {
  const rows = flatten(items);
  if (rows.length === 0) return [];

  const labelOf = new Map(spaces.map((s) => [s.code, s.label]));
  const order = new Map(spaces.map((s, i) => [s.code, i]));

  const bucket = new Map<string | null, SpaceTotal>();
  for (const row of rows) {
    const code = row.space_code || null;
    const found = bucket.get(code);
    const next = found ?? {
      code,
      // 목록에서 빠진 코드도 코드를 그대로 보여 준다 — 지운 코드가 남아 있어도
      // 빈칸이 되면 자료가 사라진 것처럼 읽힌다(`resolveLabel` 과 같은 원칙).
      label:
        code === null
          ? UNASSIGNED_SPACE_LABEL
          : (labelOf.get(code) ?? code),
      amount: 0,
      material_amount: 0,
      labor_amount: 0,
      count: 0,
    };
    next.amount += row.amount;
    next.material_amount += row.material_amount;
    next.labor_amount += row.labor_amount;
    next.count += 1;
    bucket.set(code, next);
  }

  const LAST = Number.MAX_SAFE_INTEGER;
  return [...bucket.values()].sort((a, b) => {
    // 미지정은 늘 맨 끝. 그다음이 목록 밖 코드, 그 앞이 회사 목록 순서다.
    const rank = (t: SpaceTotal): number =>
      t.code === null ? LAST : (order.get(t.code) ?? LAST - 1);
    return rank(a) - rank(b) || a.label.localeCompare(b.label);
  });
};

/** 공간을 하나라도 정한 견적인가. 아니면 화면에 표를 띄우지 않는다. */
export const hasAnySpace = (items: readonly SpaceCountable[]): boolean =>
  flatten(items).some((row) => (row.space_code ?? "") !== "");
